import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getEmail, replyToEmail, updateEmail } from '../api';

export default function EmailDetailPage() {
  const { id } = useParams();
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    getEmail(id)
      .then((data) => setEmail(data.email))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim()) return;

    setSending(true);
    setError('');
    setSuccess('');

    try {
      const result = await replyToEmail(id, replyBody);
      setSuccess(`Reply sent to ${result.sentTo} from ${result.sentFrom}`);
      setReplyBody('');
      // Refresh email to show updated status
      const updated = await getEmail(id);
      setEmail(updated.email);
    } catch (err) {
      setError(err.message || 'Failed to send reply.');
    } finally {
      setSending(false);
    }
  };

  const handleArchive = async () => {
    try {
      await updateEmail(id, { status: 'archived' });
      setEmail((prev) => ({ ...prev, status: 'archived' }));
    } catch {
      // handled
    }
  };

  if (loading) return <div className="loading">Loading email...</div>;
  if (!email) return <div className="empty-state"><p>Email not found.</p></div>;

  return (
    <div className="email-detail">
      <Link to="/emails" className="back-link">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
        Back to inbox
      </Link>

      <div className="page-header">
        <h1>{email.subject || '(No subject)'}</h1>
      </div>

      <div className="email-meta">
        <div>
          <strong>From:</strong> {email.fromName ? `${email.fromName} <${email.fromAddress}>` : email.fromAddress}
        </div>
        <div>
          <strong>To:</strong> {email.toAddress}
        </div>
        <div>
          <strong>Date:</strong> {new Date(email.createdAt).toLocaleString('en-ZA')}
        </div>
        <div>
          <span className={`badge badge-${statusBadge(email.status)}`}>{email.status}</span>
          <span className={`badge badge-${categoryBadge(email.category)}`} style={{ marginLeft: 6 }}>{email.category}</span>
        </div>
      </div>

      <div className="email-body">
        {email.bodyText || '(No content)'}
      </div>

      {/* Previous reply */}
      {email.replyBody && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header">
            <span className="card-title">Your reply</span>
            <span style={{ fontSize: 12, color: '#888' }}>
              {email.repliedAt && new Date(email.repliedAt).toLocaleString('en-ZA')}
            </span>
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {email.replyBody}
          </div>
        </div>
      )}

      {/* Reply box */}
      <div className="reply-box">
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
          {email.replyBody ? 'Send another reply' : 'Reply'}
        </h3>

        {error && <div className="error-msg">{error}</div>}
        {success && <div className="success-msg">{success}</div>}

        <form onSubmit={handleReply}>
          <textarea
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            placeholder={`Reply to ${email.fromName || email.fromAddress}...`}
          />
          <div className="reply-actions">
            <button type="submit" className="btn btn-primary" disabled={sending || !replyBody.trim()}>
              {sending ? 'Sending...' : 'Send reply'}
            </button>
            {email.status !== 'archived' && (
              <button type="button" className="btn btn-secondary" onClick={handleArchive}>
                Archive
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function statusBadge(status) {
  switch (status) {
    case 'unread': return 'blue';
    case 'read': return 'gray';
    case 'replied': return 'green';
    case 'archived': return 'gray';
    default: return 'gray';
  }
}

function categoryBadge(cat) {
  switch (cat) {
    case 'support': return 'blue';
    case 'legal': return 'purple';
    case 'privacy': return 'yellow';
    default: return 'gray';
  }
}
